import React from 'react';
// On utilise Framer Motion pour les animations fluides
import { motion } from 'framer-motion'; 
// Quelques icônes pour la déco
import { BiWifi, BiWorld, BiRadar } from 'react-icons/bi';
// Ton image de fond
import Banniere from '../img/mer.png'; 

const Watch = () => {
  const sources = [
    {
      name: "Daily Dev",
      type: "NEWSLETTER",
      color: "text-discord-blurple bg-discord-blurple/10",
      desc: "Ma source principale pour les actus, conseils pratiques et les dernières tendances dans le monde du développement.",
      link: "https://daily.dev"
    },
    {
      name: "Pro ROBOTS",
      type: "YOUTUBE",
      color: "text-red-600 bg-red-100 dark:bg-red-900/20",
      desc: "Chaîne dédiée aux avancées en IA appliquée à la robotique. Même si ce n'est pas du pur web, ça me rappelle ma passion initiale.",
      link: "https://www.youtube.com/@PROROBOTS"
    },
    {
      name: "Coursera / CNED",
      type: "ACADÉMIQUE",
      color: "text-blue-600 bg-blue-100 dark:bg-blue-900/20",
      desc: "Je suis des cours d'universités renommées (Harvard, MIT) pour consolider mes bases théoriques en informatique.",
      link: "https://www.coursera.org"
    },
    {
      name: "FreeCodeCamp",
      type: "PRATIQUE",
      color: "text-gray-800 bg-gray-200 dark:text-gray-200 dark:bg-white/10",
      desc: "Exercices pratiques sur le développement web, l'IA et les maths appliquées à l'informatique.",
      link: "https://www.freecodecamp.org"
    },
    {
      name: "Google News",
      type: "INTERNATIONAL",
      color: "text-green-600 bg-green-100 dark:bg-green-900/20",
      desc: "Lecture d'articles exclusivement en anglais sur l'IA, la blockchain et la crypto pour améliorer mon niveau de langue.",
      link: "https://news.google.com"
    },
    {
      name: "Le Monde Informatique",
      type: "TENDANCES",
      color: "text-orange-600 bg-orange-100 dark:bg-orange-900/20",
      desc: "Newsletter découverte via le CNED pour suivre les tendances tech et les événements majeurs du secteur.",
      link: "https://www.lemondeinformatique.fr"
    }
  ];

  // Configuration de l'animation en cascade (Stagger)
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="pt-24 pb-0 overflow-x-hidden">
      
      {/* SECTION 1 : HEADER AVEC EFFET RADAR */}
      <section className="max-w-6xl mx-auto px-6 py-16 relative">
        
        {/* Éléments décoratifs flottants (Icônes d'ambiance) */}
        <div className="absolute top-0 right-10 opacity-10 dark:opacity-20 hidden md:block">
            <motion.div animate={{ y: [0, -20, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}>
                <BiWorld size={100} />
            </motion.div>
        </div>
        <div className="absolute bottom-10 left-10 opacity-10 dark:opacity-20 hidden md:block">
            <motion.div animate={{ y: [0, 20, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}>
                <BiWifi size={80} />
            </motion.div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-end gap-6 relative z-10">
          <div className="text-center md:text-left">
            <div className="relative inline-block">
                {/* Onde Radar animée derrière le titre */}
                <span className="absolute -top-6 -right-8 flex h-20 w-20">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-discord-blurple opacity-20"></span>
                    <span className="relative inline-flex rounded-full h-20 w-20 border-2 border-discord-blurple/10"></span>
                </span>

                <h2 className="text-5xl font-black mb-4 italic tracking-tighter uppercase relative z-10">
                  Radar Tech 📡
                </h2>
            </div>
            
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl">
              L'informatique évolue vite. Voici mes canaux quotidiens pour nourrir ma curiosité, de l'IA à la robotique.
            </p>
          </div>
          
          <div className="hidden md:block">
            <motion.div 
                whileHover={{ scale: 1.05 }}
                className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border border-green-200 dark:border-green-800 shadow-sm"
            >
              <span className="relative flex h-3 w-3 mr-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
              </span>
              Veille Active
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 2 : GRILLE DE SOURCES ANIMÉE */}
      <section className="w-full bg-gray-50 dark:bg-[#0c0c0c] border-y border-gray-100 dark:border-white/5 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {sources.map((source, index) => (
              <motion.a 
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.02, y: -5 }}
                href={source.link} 
                target="_blank" 
                rel="noreferrer"
                className="group flex flex-col p-8 rounded-3xl bg-white dark:bg-discord-gray border border-gray-100 dark:border-white/10 hover:border-discord-blurple hover:shadow-2xl transition-all duration-300"
              >
                <div className="flex justify-between items-start mb-6">
                  <span className={`text-[10px] font-bold tracking-wider px-3 py-1 rounded-full uppercase ${source.color}`}>
                    {source.type}
                  </span>
                  <span className="text-gray-300 dark:text-gray-600 group-hover:text-discord-blurple transition-colors">
                    ↗
                  </span>
                </div>
                
                <h3 className="text-xl font-bold mb-3 group-hover:text-discord-blurple transition-colors">
                  {source.name}
                </h3>
                
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed flex-grow">
                  {source.desc}
                </p>
              </motion.a>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SECTION 3 : PHILOSOPHIE IMMERSIVE */}
      <section className="w-full relative h-[400px] flex items-center justify-center overflow-hidden border-t border-white/5">
        
        {/* IMAGE DE FOND (Parallaxe simple) */}
        <div 
          className="absolute inset-0 z-0 scale-105"
          style={{
            backgroundImage: `url(${Banniere})`, 
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'grayscale(10%) brightness(1.6)', // Ton réglage actuel
          }}
        ></div>
        
        {/* OVERLAY DÉGRADÉ */}
        <div className="absolute inset-0 bg-discord-gray/10 dark:bg-black/70 z-10"></div>

        {/* CONTENU CENTRÉ AVEC ANIMATION D'ENTRÉE */}
        <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="relative z-20 max-w-4xl mx-auto px-6"
        >
          <div className="p-10 rounded-[3rem] bg-white/100 backdrop-blur-md border border-white/20 text-center relative overflow-hidden group shadow-2xl">
            <div className="absolute top-0 left-0 w-2 h-full bg-discord-blurple opacity-80 group-hover:w-full transition-all duration-700 ease-in-out z-0"></div>
            <p className="text-2xl italic font-medium text-black group-hover:text-white transition-colors duration-500 relative z-10 leading-relaxed drop-shadow-md">
              "Je reste à jour sur l'actu numérique non seulement pour mes projets, mais surtout par passion pour les innovations comme l'IA et la Bio-Informatique."
            </p>
          </div>
        </motion.div>
      </section>

    </div>
  );
};

export default Watch;